import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import LoadingPlaceholder from './LoadingPlaceholder';
import { CURRENCY, getCurrencySymbol } from '../../../../constants/currency';
import PriceBreakdownRow from './PriceBreakdownRow';

const propTypes = {
  loading: PropTypes.bool,
  pricing: PropTypes.shape({
    total_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    total_shipping_cost: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    total_tax: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    total_discount_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
  }),
  discount: PropTypes.number,
  discountLabel: PropTypes.string,
  freeShipping: PropTypes.bool,
  waivedShipping: PropTypes.number,
  className: PropTypes.string,
};

const PriceBreakdownList = ({
  loading,
  pricing,
  discount: discountOverride,
  discountLabel,
  freeShipping,
  waivedShipping,
  className,
}) => {
  const classes = classNames({
    'price-breakdown': true,
    'is-loading': loading,
    [className]: !!className,
  });

  if (loading || !pricing) {
    return <LoadingPlaceholder className={classes} />;
  }

  const total = pricing.total_price.value;
  const tax = pricing.total_tax.value;
  const shipping = pricing.total_shipping_cost.value;
  const discount = discountOverride ?? pricing.total_discount_price?.value ?? 0;
  // `total_price` is already discounted, so the subtotal is built back up to
  // its pre-discount value in order for the discount row to add up.
  const subTotal = total - tax - shipping + discount;
  const symbol = getCurrencySymbol(pricing.total_price.currency);
  const shippingIsWaived = freeShipping && shipping === 0;

  const rows = [
    {
      label: 'Subtotal',
      value: subTotal,
    },
    ...(discount > 0
      ? [
          {
            label: discountLabel ?? 'Discount',
            value: -discount,
            discount: true,
          },
        ]
      : []),
    {
      label: 'Taxes',
      value: tax,
    },
    {
      label: 'Shipping & handling',
      value: shipping,
      ...(shippingIsWaived
        ? { valueText: 'Free', struckValue: waivedShipping, discount: true }
        : {}),
    },
    {
      label: 'Total',
      value: total,
    },
  ];

  return (
    <div className={classes}>
      {rows.map(row => (
        <PriceBreakdownRow
          key={row.label}
          label={row.label}
          currencySymbol={symbol}
          value={row.value}
          valueText={row.valueText}
          struckValue={row.struckValue}
          discount={row.discount}
        />
      ))}
    </div>
  );
};

PriceBreakdownList.propTypes = propTypes;

export default PriceBreakdownList;
